import type {
  MandatoryFieldDefault,
  ProviderConfig,
} from "@/lib/types/itsm-config";

export interface MandatoryRowProblems {
  missingField: boolean;
  missingValue: boolean;
  duplicate: boolean;
}

const rowKey = (row: MandatoryFieldDefault) =>
  JSON.stringify([row.table.trim(), row.field.trim()]);

/** What is wrong with each row, in order. The backend rejects a row with no
 *  field, or with no value for predefined/likely, and applies only the last of
 *  two rows for the same table and field. */
export function mandatoryRowProblems(
  rows: MandatoryFieldDefault[],
): MandatoryRowProblems[] {
  const seen = new Map<string, number>();
  for (const row of rows) {
    if (row.field.trim()) {
      seen.set(rowKey(row), (seen.get(rowKey(row)) ?? 0) + 1);
    }
  }
  return rows.map((row) => ({
    missingField: !row.field.trim(),
    missingValue: row.strategy !== "llm" && !row.value?.trim(),
    duplicate: (seen.get(rowKey(row)) ?? 0) > 1,
  }));
}

/** A message naming the first invalid row, or null when every row is valid.
 *  Checked before saving so one incomplete row doesn't fail the whole page's
 *  save with a raw validation error. */
export function mandatoryFieldsSaveError(draft: ProviderConfig): string | null {
  const problems = mandatoryRowProblems(draft.mandatory_field_defaults ?? []);
  const index = problems.findIndex(
    (p) => p.missingField || p.missingValue || p.duplicate,
  );
  if (index === -1) return null;
  // eslint-disable-next-line security/detect-object-injection -- index from findIndex on the same array
  const problem = problems[index];
  const reason = problem.missingField
    ? "needs a field"
    : problem.missingValue
      ? "needs a value for its strategy"
      : "repeats another row's field";
  return `Mandatory field defaults: row ${String(index + 1)} ${reason}.`;
}
